import type { ReactNode } from 'react';

import { Button, ButtonSpinner, ButtonText } from '@/components/ui/button';
import { useColors } from '@/hooks/useColors';

type ButtonAction = 'primary' | 'secondary' | 'positive' | 'negative' | 'default';

type ButtonVariant = 'solid' | 'outline' | 'link';

type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface LoaderButtonProps {
  /** When true, renders a spinner inside the button and disables it. */
  isLoading?: boolean;
  /** Disabled independent of loading. The button is also disabled
   * whenever `isLoading` is true regardless of this prop. */
  isDisabled?: boolean;
  onPress?: () => void;
  action?: ButtonAction;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Plain text content wraps in `ButtonText` automatically. Pass
   * a JSX node directly when more structure is needed (e.g. an
   * icon alongside the label). */
  children: ReactNode;
  /** Accessibility label; pass a loading-aware label when the
   * button's text doesn't change between states. */
  'aria-label'?: string;
  className?: string;
}

/**
 * Button with a built-in loading spinner that respects the
 * design-system color tokens and dark mode.
 *
 * Picks the spinner color based on `variant` so it stays legible
 * against the button's surface in both themes:
 * - `solid` (filled primary background) → `typography-0` (the
 *   inverted-on-primary surface).
 * - `outline` / `link` (transparent or background-tinted surface)
 *   → `primary-200`, matching the text and border color.
 *
 * Used today in `StatementTriageSheet`; the existing inline
 * `ButtonSpinner` pattern (~7 other call sites) can migrate to this
 * component as those screens are next touched.
 */
export function LoaderButton({
  isLoading = false,
  isDisabled = false,
  onPress,
  action = 'primary',
  variant = 'solid',
  size = 'sm',
  children,
  className,
  ...rest
}: LoaderButtonProps) {
  const { colors } = useColors();

  const spinnerColor = variant === 'solid' ? colors.typography[0] : colors.primary[200];

  const disabled = isDisabled || isLoading;

  return (
    <Button
      {...rest}
      onPress={onPress}
      action={action}
      variant={variant}
      size={size}
      isDisabled={disabled}
      accessibilityState={{ disabled }}
      className={className}>
      {isLoading ? <ButtonSpinner color={spinnerColor} /> : null}
      {typeof children === 'string' ? <ButtonText>{children}</ButtonText> : children}
    </Button>
  );
}
